import axios, { AxiosInstance } from 'axios';
import { message } from 'antd';
import { stringify } from 'query-string';
import {
  DataProvider,
  HttpError,
  CrudOperators,
  CrudFilters,
  CrudSorting,
} from '@pankod/refine-core';
import { responseErrorHandler } from './authProvider';
import UserHandler from 'util/UserHandler';

const axiosInstance = axios.create();

axiosInstance.interceptors.request.use(
  config => {
    // Inserimento del token
    if(!config.headers) config.headers = {};
    config.headers.authorization = `Bearer ${UserHandler.getInstance().token}`;
    return config;
  },
  error => {
    message.error(error.message);
    const customError: HttpError = {
      ...error,
      message: error.response?.data?.message,
      statusCode: error.response?.status,
    };
    return Promise.reject(customError);
  },
);

axiosInstance.interceptors.response.use(
  response => {
    return response.data;
  },
  async error => {
    // Handler per 401 altrimenti prosegue segnalando l'errore e tornando alla login
    responseErrorHandler(error.response);
    const customError: HttpError = {
      ...error,
      message: error.response?.data?.message,
      statusCode: error.response?.status,
    };
    return Promise.reject(customError);
  },
);

const mapOperator = (operator: CrudOperators): string => {
  switch (operator) {
    case 'ne':
    case 'gte':
    case 'lte':
      return `_${operator}`;
    case 'contains':
      return '_like';
    case 'eq':
    default:
      return '';
  }
};

const generateSort = (sort?: CrudSorting) => {
  if (sort && sort.length > 0) {
    const _sort: string[] = [];
    const _order: string[] = [];

    sort.map(item => {
      _sort.push(item.field);
      _order.push(item.order);
    });

    return {
      _sort,
      _order,
    };
  }

  return;
};

const addCustomFields = async (variables: any) => {
  return variables;
}

const generateFilter = (filters?: CrudFilters) => {
  const queryFilters: { [key: string]: string } = {};
  if (filters) {
    filters.map(filter => {
      if (filter.operator !== 'or') {
        const { field, operator, value } = (filter as any);

        if (field === 'q') {
          queryFilters[field] = value;
          return;
        }

        const mappedOperator = mapOperator(operator);
        queryFilters[`${field}${mappedOperator}`] = value;
      }
    });
  }
  const parent_user = UserHandler.getInstance().user?.id;
  if (!queryFilters.parent_user && parent_user) {
    queryFilters.parent_user = '' + parent_user;
  }

  return queryFilters;
};

// interface ITableVariables extends TVariables {
//   table: string
// }

const ApiProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axiosInstance,
): DataProvider => ({
  getList: async ({ resource, pagination, filters, sort }) => {
    const url = `${apiUrl}/${resource}`;
    // pagination
    const current = pagination?.current || 1;
    const pageSize = pagination?.pageSize || 10;

    const queryFilters = generateFilter(filters);

    const query: {
      _start: number;
      _end: number;
      _sort?: string;
      _order?: string;
    } = {
      _start: (current - 1) * pageSize,
      _end: current * pageSize,
    };

    const generatedSort = generateSort(sort);
    if (generatedSort) {
      const { _sort, _order } = generatedSort;
      query._sort = _sort.join(',');
      query._order = _order.join(',');
    }

    const { data } = await httpClient.get(
      `${url}?${stringify(query)}&${stringify(queryFilters)}`,
    );

    const total = /*+headers["x-total-count"]*/ data.length;

    return {
      data,
      total,
    };
  },

  getMany: async ({ resource, ids }) => {
    const formData = await addCustomFields(null);
    const { data } = await httpClient.get(
      `${apiUrl}/${resource}?${stringify({ id: ids })}`,
      {params: { store_id: formData?.store_id || formData?.store?.store_id }}
    );

    return {
      data,
    };
  },

  create: async ({ resource, variables }) => {
    const url = `${apiUrl}/${resource}`;
    let formData: any = variables;
    if ((<any>variables).table) {
      formData = { [(<any>variables).table]: variables };
    }
    const { data } = await httpClient.post(url, await addCustomFields(formData));

    return {
      data,
    };
  },

  createMany: async ({ resource, variables }) => {
    const response = await Promise.all(
      variables.map(async param => {
        let formData: any = param;
        if ((<any>param).table) {
          formData = { [(<any>param).table]: param };
        }
        const { data } = await httpClient.post(
          `${apiUrl}/${resource}`,
          await addCustomFields(formData),
        );
        return data;
      }),
    );

    return { data: response };
  },

  update: async ({ resource, id, variables }) => {
    const url = `${apiUrl}/${resource}/${id}`;
    let formData: any = variables;
    if ((<any>variables).table) {
      formData = { [(<any>variables).table]: variables };
    }
    const { data } = await httpClient.patch(url, await addCustomFields(formData));
    return {
      data,
    };
  },

  updateMany: async ({ resource, ids, variables }) => {
    const response = await Promise.all(
      ids.map(async id => {
        let formData: any = variables;
        if ((<any>variables).table) {
          formData = { [(<any>variables).table]: variables };
        }
        const { data } = await httpClient.patch(
          `${apiUrl}/${resource}/${id}`,
          await addCustomFields(formData),
        );
        return data;
      }),
    );

    return { data: response };
  },

  getOne: async ({ resource, id }) => {
    const url = `${apiUrl}/${resource}/${id}`;
    const formData = await addCustomFields(null);
    const { data } = await httpClient.get(url, {params: { store_id: formData?.store_id || formData?.store?.store_id }});

    return {
      data,
    };
  },

  deleteOne: async ({ resource, id, variables }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    const formData = await addCustomFields(variables);

    const { data } = await httpClient.delete(url, {data: formData});

    return {
      data,
    };
  },

  deleteMany: async ({ resource, ids, variables }) => {
    const response = await Promise.all(
      ids.map(async id => {
        const { data } = await httpClient.delete(
          `${apiUrl}/${resource}/${id}`,
          {data: await addCustomFields(variables)},
        );
        return data;
      }),
    );
    return { data: response };
  },

  getApiUrl: () => {
    return apiUrl;
  },

  custom: async ({ url, method, filters, sort, payload, query, headers }) => {
    let requestUrl = `${url}?`;
    if (sort) {
      const generatedSort = generateSort(sort);
      if (generatedSort) {
        const { _sort, _order } = generatedSort;
        const sortQuery = {
          _sort: _sort.join(','),
          _order: _order.join(','),
        };
        requestUrl = `${requestUrl}&${stringify(sortQuery)}`;
      }
    }

    if (filters) {
      const filterQuery = generateFilter(filters);
      requestUrl = `${requestUrl}&${stringify(filterQuery)}`;
    }

    if (query) {
      requestUrl = `${requestUrl}&${stringify(query)}`;
    }

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    let axiosResponse;
    switch (method) {
      case 'put':
      case 'post':
      case 'patch':
        axiosResponse = await httpClient[method](url, payload);
        break;
      case 'delete':
        axiosResponse = await httpClient.delete(url);
        break;
      default:
        axiosResponse = await httpClient.get(requestUrl);
        break;
    }

    const { data } = axiosResponse;

    return Promise.resolve({ data });
  },
});

export default ApiProvider;
